import { Hash, VolumeFull } from "@styled-icons/boxicons-regular";
import { observer } from "mobx-react-lite";
import { Channel } from "cayble.js";

import fallback from "./assets/group.png";

import { useClient } from "../../controllers/client/ClientController";
import { ImageIconBase, IconBaseProps } from "./IconBase";

interface Props extends IconBaseProps<Channel> {
    isServerChannel?: boolean;
}

const EmojiIcon = observer(
    ({
        emoji,
        size,
        ...rest
    }: {
        emoji: string;
        size?: number;
    } & JSX.HTMLAttributes<HTMLDivElement>) => (
        <div
            {...rest}
            role="img"
            aria-label={emoji}
            style={{
                width: size,
                height: size,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: (size ?? 24) * 0.62,
                lineHeight: 1,
                userSelect: "none",
            }}>
            {emoji}
        </div>
    ),
);

export default observer(
    (
        props: Props &
            Omit<
                JSX.HTMLAttributes<HTMLImageElement>,
                keyof Props | "children" | "as"
            >,
    ) => {
        const client = useClient();

        const {
            size,
            target,
            attachment,
            isServerChannel: server,
            animate,
            ...imgProps
        } = props;

        const rawIcon = target?.icon ?? attachment ?? undefined;
        let iconURL: string | undefined;
        let emoji: string | undefined;
        const raw: any = rawIcon;

        if (typeof raw === "object" && raw && raw._id) {
            iconURL = client.generateFileURL(
                raw,
                { max_side: 256 },
                animate,
            );
        } else if (typeof raw === "string" && raw.trim()) {
            const s = raw.trim();
            // Legacy autumn file id stored as a raw string -> build icons URL.
            if (/^[A-Za-z0-9_-]{12,}$/.test(s)) {
                const autumn = client.configuration?.features.autumn;
                if (autumn?.enabled) iconURL = `${autumn.url}/icons/${s}`;
            } else {
                emoji = s;
            }
        }

        const isServerChannel =
            server ||
            (target &&
                (target.channel_type === "TextChannel" ||
                    target.channel_type === "VoiceChannel"));

        if (emoji) {
            return <EmojiIcon emoji={emoji} size={size} {...(imgProps as any)} />;
        }

        if (typeof iconURL === "undefined") {
            if (isServerChannel) {
                if (target?.channel_type === "VoiceChannel") {
                    return <VolumeFull size={size} />;
                }
                return <Hash size={size} />;
            }
        }

        // The border radius of the channel icon, if it's a server-channel it should be square (undefined).
        let borderRadius: string | undefined = "--border-radius-channel-icon";
        if (isServerChannel) {
            borderRadius = undefined;
        }

        return (
            // ! TODO: replace fallback with <picture /> + <source />
            <ImageIconBase
                {...imgProps}
                width={size}
                height={size}
                loading="lazy"
                aria-hidden="true"
                borderRadius={borderRadius}
                src={iconURL ?? fallback}
            />
        );
    },
);
